import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getApiBasePath,
  lookupDocuments,
  type SupportDocument,
} from '../lib/support-api';
import { PageBody, PageHead } from '../components/Page';
import { StatusBanner } from '../components/StatusBanner';
import { Badge, type Tone } from '../components/Pill';
import { intlLocale } from '../i18n/formats';
import { useLocale } from '../i18n/LocaleProvider';
import type { SupportedLocale } from '../i18n';

/**
 * Catalogue key for each document kind.
 *
 * These three ARE translated, unlike the statuses beside them: `INVOICE` is our own word for a
 * thing the merchant calls a Rechnung, not an API token an operator matches against Sentry.
 */
const KIND_LABEL_KEY: Record<string, string> = {
  INVOICE: 'documents.kind.invoice',
  CREDIT_NOTE: 'documents.kind.creditNote',
  CANCELLATION_INVOICE: 'documents.kind.cancellation',
};

/** Badge tone per kind. A Storno is the one that changes what the other rows mean. */
const KIND_TONE: Record<string, Tone> = {
  INVOICE: 'sky',
  CREDIT_NOTE: 'teal',
  CANCELLATION_INVOICE: 'amber',
};

/**
 * Document statuses that mean the merchant never got the document.
 *
 * Everything else on `DocumentStatus` is either in flight or fine; these are the states a
 * merchant writes in about, so they are the ones that get colour. `EINVOICE_INCOMPLETE` waits on
 * data the merchant has to add through its E_INVOICE_REQUIRED item. Mirrors
 * `NEEDS_ATTENTION_STATUSES` in `libs/types`; the ops dashboard does not depend on that library.
 */
const FAILED_STATUSES = new Set([
  'PDF_FAILED',
  'EINVOICE_INCOMPLETE',
  'EMAIL_FAILED',
  'EMAIL_BOUNCED',
]);

/** Date only — a document is identified by its issue date, not its minute. */
function issued(value: string, locale: SupportedLocale): string {
  return new Date(value).toLocaleDateString(intlLocale(locale), {
    dateStyle: 'medium',
  });
}

/**
 * The agent's document lookup.
 *
 * Exists because a support ticket in this product is almost always ABOUT a document — "RE-26392
 * shows 9,18 where it should show 9,19" — and until now there was no way to look one up: the
 * agent had the number the merchant quoted and nowhere to type it.
 *
 * State lives in the URL, so the link the ticket page hands over ("search this shop's
 * documents") arrives already scoped, and a result an agent found can be pasted to a colleague.
 */
export function DocumentLookupPage() {
  const { t, locale } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const shopId = searchParams.get('shop') ?? '';
  const submitted = searchParams.get('q') ?? '';

  // The box is local and the URL is the submitted query: a lookup with no shop is a
  // cross-shop scan, so it runs when the agent says so rather than on every keystroke.
  const [term, setTerm] = useState(submitted);

  const results = useQuery({
    queryKey: ['support-documents', submitted, shopId],
    queryFn: () => lookupDocuments(submitted, shopId || undefined),
    enabled: submitted.trim().length > 0,
    placeholderData: keepPreviousData,
  });

  /** Writes the term into the URL, which is what actually runs the query. */
  function submit(next: string): void {
    const params = new URLSearchParams();
    if (next.trim()) params.set('q', next.trim());
    if (shopId) params.set('shop', shopId);
    setSearchParams(params);
  }

  /** Drops the shop scope, keeping the term. */
  function clearShopScope(): void {
    const params = new URLSearchParams();
    if (submitted) params.set('q', submitted);
    setSearchParams(params);
  }

  const documents: SupportDocument[] = results.data?.data ?? [];

  return (
    <>
      <PageHead
        eyebrow={t('documents.eyebrow')}
        title={t('documents.title')}
        lead={t('documents.lead')}
      />

      <PageBody>
        <div className="surface p-4 space-y-3">
          <div className="flex flex-wrap gap-3 items-center">
            <input
              type="search"
              value={term}
              onChange={(entry) => setTerm(entry.target.value)}
              onKeyDown={(entry) => entry.key === 'Enter' && submit(term)}
              placeholder={t('documents.placeholder')}
              aria-label={t('documents.searchLabel')}
              className="field-input flex-1 min-w-[min(240px,100%)]"
            />
            <button
              onClick={() => submit(term)}
              disabled={!term.trim()}
              className="btn-primary btn-sm"
            >
              {t('documents.search')}
            </button>
          </div>

          {shopId ? (
            <p className="text-xs text-gray-600">
              {t('documents.scoped')}{' '}
              <button
                onClick={clearShopScope}
                className="font-medium text-primary-700 hover:text-primary-800"
              >
                {t('documents.clearScope')}
              </button>
            </p>
          ) : (
            <p className="text-xs text-gray-500">{t('documents.unscoped')}</p>
          )}
        </div>

        {results.data?.truncated ? (
          <StatusBanner tone="warning">{t('documents.truncated')}</StatusBanner>
        ) : null}

        <div className="surface overflow-x-auto">
          {!submitted.trim() ? (
            <div className="p-8 text-center text-gray-500">
              {t('documents.prompt')}
            </div>
          ) : results.isLoading ? (
            <div className="p-8 text-center text-gray-500">
              {t('documents.searching')}
            </div>
          ) : results.isError ? (
            <div className="p-4">
              <StatusBanner tone="error">
                {t('documents.loadError')}
              </StatusBanner>
            </div>
          ) : documents.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {t('documents.noMatch', { term: submitted })}
            </div>
          ) : (
            // Below `sm`, one column: the document with its shop, order, date, amount, state and
            // PDF link underneath, rather than a 860px table whose number column was squeezed to
            // three lines while the shop and the PDF link sat off screen.
            <table className="data-table table-fixed sm:table-auto sm:min-w-[860px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-3">
                    {t('documents.columns.document')}
                  </th>
                  <th className="hidden sm:table-cell text-left px-4 py-3">
                    {t('documents.columns.shop')}
                  </th>
                  <th className="hidden sm:table-cell text-left px-4 py-3">
                    {t('documents.columns.order')}
                  </th>
                  <th className="hidden sm:table-cell text-left px-4 py-3">
                    {t('documents.columns.issued')}
                  </th>
                  <th className="hidden sm:table-cell text-right px-4 py-3">
                    {t('documents.columns.gross')}
                  </th>
                  <th className="hidden sm:table-cell text-left px-4 py-3">
                    {t('documents.columns.state')}
                  </th>
                  <th className="hidden sm:table-cell text-left px-4 py-3">
                    {t('documents.columns.pdf')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {documents.map((document) => (
                  <tr key={`${document.kind}-${document.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <Badge tone={KIND_TONE[document.kind] ?? 'gray'}>
                          {KIND_LABEL_KEY[document.kind]
                            ? t(KIND_LABEL_KEY[document.kind])
                            : document.kind}
                        </Badge>
                        <span className="font-medium text-gray-900 break-all sm:whitespace-nowrap sm:break-normal">
                          {document.documentNumber}
                        </span>
                      </div>
                      {/* The frozen tax facts, because "the VAT is wrong" is the commonest
                          thing a merchant writes in about and this is the answer to it. Both
                          values are API tokens and stay untranslated, like every other one. */}
                      <div className="text-gray-500 text-xs mt-0.5">
                        {document.taxTreatmentKind ??
                          t('documents.noTreatment')}
                        {document.jurisdictionLabel
                          ? ` · ${document.jurisdictionLabel}`
                          : ''}
                        {document.eInvoiceOutcome
                          ? ` · ${t('documents.eInvoice', {
                              outcome: document.eInvoiceOutcome,
                            })}`
                          : ''}
                      </div>
                      <div className="sm:hidden mt-1.5 space-y-0.5 text-xs text-gray-700">
                        <div className="truncate">{document.shopDomain}</div>
                        <div className="flex flex-wrap gap-x-2">
                          <span>{document.orderNumber ?? '—'}</span>
                          <span className="tabular-nums">
                            {issued(document.issueDate, locale)}
                          </span>
                          <span className="tabular-nums text-gray-900">
                            {document.totalGross} {document.currency}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-2">
                          <span
                            className={
                              FAILED_STATUSES.has(document.status)
                                ? 'text-red-600 font-medium'
                                : ''
                            }
                          >
                            {document.status}
                          </span>
                          {!document.isActive ? (
                            <span className="text-amber-700">
                              {t('documents.superseded')}
                            </span>
                          ) : null}
                          {document.pdfAvailable ? (
                            <a
                              href={`${getApiBasePath()}${document.downloadPath}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary-700 hover:text-primary-800 hover:underline"
                            >
                              {t('documents.open')}
                            </a>
                          ) : null}
                        </div>
                        {document.emailLastError ? (
                          <div className="text-red-600 break-all">
                            {document.emailLastError}
                          </div>
                        ) : null}
                      </div>
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3 text-gray-700 max-w-48 truncate">
                      {document.shopDomain}
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3 text-gray-700">
                      {document.orderNumber ?? '—'}
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3 text-gray-600 tabular-nums">
                      {issued(document.issueDate, locale)}
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3 text-right text-gray-900 tabular-nums">
                      {document.totalGross} {document.currency}
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3">
                      <span
                        className={
                          FAILED_STATUSES.has(document.status)
                            ? 'text-red-600 font-medium'
                            : 'text-gray-700'
                        }
                      >
                        {document.status}
                      </span>
                      {/* A superseded invoice still exists and is still findable by its
                          number; saying so is the difference between "we voided it" and
                          "your invoice is wrong". */}
                      {!document.isActive ? (
                        <span className="block text-amber-700 text-xs">
                          {t('documents.superseded')}
                        </span>
                      ) : null}
                      {document.emailLastError ? (
                        <span className="block text-red-600 text-xs break-all">
                          {document.emailLastError}
                        </span>
                      ) : null}
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3">
                      {document.pdfAvailable ? (
                        // Straight at the API: the per-document download routes are public and
                        // secured by the unguessable id — the same link the merchant's own app
                        // and emails hand out.
                        <a
                          href={`${getApiBasePath()}${document.downloadPath}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary-700 hover:text-primary-800 hover:underline"
                        >
                          {t('documents.open')}
                        </a>
                      ) : (
                        <span className="text-gray-400">
                          {t('documents.noPdf')}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </PageBody>
    </>
  );
}
